import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/BatchManagement.css";
import axios from "axios";
import { jsPDF } from "jspdf";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as pdfjsLib from "pdfjs-dist";
import { subscribe } from "./eventBus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faHome, faArrowLeft, faPrint, faPlus, faTrash, faPencilAlt, faCommentDots, faCheckCircle, faUndo } from "@fortawesome/free-solid-svg-icons";
import { generateReport } from "./QRReport.js"
import { batchReport } from './BatchReport.js'

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `${window.location.origin}/pdf.worker.min.js`;

/* ── tiny status helper ─────────────────────────────────────── */
function StatusPill({ batch }) {
  if (batch.recalled) return <span className="status-pill recalled">Recalled</span>;
  if (batch.status === 1) return <span className="status-pill approved">Approved</span>;
  if (batch.status === 0) return <span className="status-pill pending">Awaiting Approval</span>;
  if (batch.status === 3) return <span className="status-pill updates">Updates Awaited</span>;
  return <span className="status-pill rejected">Rejected</span>;
}

function BatchManagement() {
  const navigate = useNavigate();
  const navigateToDashboard = () => navigate('/sku-dashboard');

  const [fieldErrors, setFieldErrors] = useState({});
  const [batchno, setBatchno] = useState("");
  const [manufactureDate, setManufactureDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [filter, setFilter] = useState("");
  const [batchTable, setBatchTable] = useState({});
  const [isAdmin, setisAdmin] = useState(false);
  const [showcommentsModal, setShowCommentsModal] = useState(false);
  const [comment, setComment] = useState('');
  const [snappedPdfImage, setSnappedPdfImage] = useState(null);
  const [modalName, setModalName] = useState("");
  const [modalPlaceholder, setModalPlaceholder] = useState("");
  const [remarksErrors, setremarksErrors] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewModalTitle, setViewModalTitle] = useState("");
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [updateType, setUpdateType] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [sku, setSku] = useState({});
  const [qrId, setQrId] = useState("");
  const [skuImage, setSkuImage] = useState(null);
  const [isCustomModal, setisCustomModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const baseURL = sessionStorage.getItem('baseURL');
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [tableData, setTableData] = useState([]);

  /* ── row height calc ── */
  useEffect(() => {
    const calculateRows = () => {
      const viewportHeight = window.innerHeight;
      const header = 450;
      const usableH = viewportHeight - header - 60 - 50 - 80;
      const rows = Math.floor(usableH / 75);
      setRowsPerPage(rows >= 2 ? rows : 2);
    };
    calculateRows();

    if (sessionStorage.getItem('isAdmin') !== "true") {
      fetchNotification();
      const unsubscribe = subscribe((event) => {
        if (event.type !== "NOTIFICATION") return;
        fetchNotification();
      });
      return unsubscribe;
    }

    window.addEventListener("resize", calculateRows);
    return () => window.removeEventListener("resize", calculateRows);
  }, []);

  const fetchNotification = () => {
    axios.get(baseURL + "notifications/unread", {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(res => res.data).then(setNotifications);
  };

  useEffect(() => {
    if (notifications.length === 0) setShowNotificationModal(false);
  }, [notifications]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => { setIsModalOpen(false); setRemarks(""); };

  /* ── pdf snap ── */
  const snapPdfSection = async ({ pdfUrl, pageNumber = 1, scale = 2 }) => {
    const res = await fetch(pdfUrl);
    const buffer = await res.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale });
    const cmToPx = (cm) => (cm / 2.54) * 96 * scale;
    const snapSize = cmToPx(3);
    const x = viewport.width / 2 - snapSize / 2;
    const y = viewport.height / 2 - snapSize / 2;
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
    const crop = document.createElement("canvas");
    crop.width = crop.height = snapSize;
    crop.getContext("2d").drawImage(canvas, x, y, snapSize, snapSize, 0, 0, snapSize, snapSize);
    return crop.toDataURL("image/png");
  };

  /* ── fetch details ── */
  const fetchDetails = async () => {
    let sku_temp = JSON.parse(sessionStorage.getItem('skuId'));
    setQrId(sessionStorage.getItem('qrId'));
    setSku(sku_temp);

    try {
      const img = await snapPdfSection({
        pdfUrl: baseURL + "qr/fetchQRDoc/" + sessionStorage.getItem('qrId') + ".pdf/" + sku_temp.sku_id,
        pageNumber: 1, scale: 2
      });
      setSnappedPdfImage(img);
    } catch (err) {
      console.warn("PDF snap failed:", err);
    }

    fetchImageAsDataURL(
      baseURL + "sku/SKUImage/" + sku_temp.product_image.replaceAll("/", "-")
    ).then(setSkuImage);

    axios.get(
      baseURL + "batch/fetchBatchDetails/" + sessionStorage.getItem('qrId') + "/" + sku_temp.sku_id
    ).then((response) => {
      if (response.data.length > 0) setCurrentPage(1);
      setBatchTable(response.data);
    });

    axios.get(baseURL + "sku/fetchSku/all").then((r) => setTableData(r.data));
  };

  useEffect(() => {
    if (sessionStorage.getItem('isLoggedIn') !== "true") { navigate('/'); return; }
    if (sessionStorage.getItem('isAdmin') === "true") setisAdmin(true);

    fetchDetails();
    const unsubscribe = subscribe((event) => {
      if (event.type !== "BATCH_ADDED") return;
      fetchDetails();
    });
    return unsubscribe;
  }, [navigate]);

  /* ── toast ── */
  function showToast(message, type = "success") {
    const toast = document.createElement("div");
    toast.innerText = message;
    Object.assign(toast.style, {
      position: "fixed", top: "20px", right: "20px",
      background: type === "error" ? "#C0392B" : type === "success" ? "#1A6B34" : "#9A5200",
      color: "#fff", padding: "12px 18px", borderRadius: "8px",
      boxShadow: "0 4px 16px rgba(0,0,0,.2)", zIndex: 9999,
      opacity: "0", transition: "opacity .3s ease", fontSize: "13px", fontFamily: "inherit"
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => (toast.style.opacity = "1"));
    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 300);
    }, 3500);
  }

  /* ── submit batch ── */
  const submitBatchDetails = (man, exp) => {
    axios.post(baseURL + 'batch/addBatchDetails', {
      batchNo: batchno, dom: man, doe: exp, qrID: qrId, sku_id: sku.sku_id,
      groupid: sessionStorage.getItem('group_id'),
      name: sessionStorage.getItem("name"),
      designation: "User",
      uid: sessionStorage.getItem('qrUid'), sku_name: sku.brand_name, emp_id: sessionStorage.getItem('username')
    }, { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
    ).then(() => {
      showToast("Batch added successfully");
      document.getElementById("addBatch").disabled = false;
      document.getElementById("batchno").attributes.removeNamedItem("style");
      setBatchno(""); setManufactureDate(""); setExpiryDate("");
      fetchDetails();
    }).catch(() => {
      setBatchno("");
      showToast("Error adding batch. Please try again.", "error");
    });
  };

  const parseYearMonth = (ym) => { const [y, m] = ym.split("-").map(Number); return new Date(y, m - 1, 1); };
  // const getCurrentMonthStart = () => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); };

  /* ── add batch handler ── */
  const handleAddBatch = async () => {
    document.getElementById("addBatch").disabled = true;
    document.getElementById("batchno").style.cursor = "not-allowed";
    const errors = {};

    const fmt = (d) => new Intl.DateTimeFormat("sv-SE", {
      timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit"
    }).format(new Date(d)).slice(0, 7);

    const man = manufactureDate ? fmt(manufactureDate) : "";
    const exp = expiryDate ? fmt(expiryDate) : "";

    if (!batchno.trim()) errors.batchno = "Batch number is required.";
    if (batchno.trim().indexOf(" ") > -1) errors.batchno = "Batch number cannot have space"
    if (!man.trim()) errors.manufactureDate = "Manufacturing date is required.";
    if (!exp.trim()) errors.expiryDate = "Expiry date is required.";

    let mfgDate = null, expDate = null;
    if (man.trim()) {
      mfgDate = parseYearMonth(man.trim());
      // if (mfgDate < getCurrentMonthStart()) errors.manufactureDate = "Manufacturing date cannot be a past date.";
      const minAllowedMfgDate = new Date(2024, 0, 1);
      if (mfgDate < minAllowedMfgDate) errors.manufactureDate = "Manufacturing date cannot be before January 2024.";
      if (mfgDate > new Date()) errors.manufactureDate = "Manufacturing date cannot be a future date.";
    }
    if (exp.trim()) expDate = parseYearMonth(exp.trim());
    if (mfgDate && expDate && mfgDate > expDate) errors.expiryDate = "Expiry date must be after manufacturing date.";

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      document.getElementById("addBatch").disabled = false;
      document.getElementById("batchno").style.cursor = "pointer";
      return;
    }

    try {
      const response = await axios.get(baseURL + `batch/checkBatch/${qrId}/${sku.sku_id}/${batchno}`);
      if (response.data.result === "Already Exists") {
        setFieldErrors({ batchno: "Batch number already exists." });
        document.getElementById("addBatch").disabled = false;
        document.getElementById("batchno").style.cursor = "pointer";
        return;
      }
      submitBatchDetails(man, exp);
    } catch {
      setFieldErrors({ batchno: "Unable to verify batch number. Please try again." });
      document.getElementById("addBatch").disabled = false;
      document.getElementById("batchno").style.cursor = "pointer";
    }
  };

  /* ── table helpers ── */
  const filteredTable = Object.values(batchTable).filter(item =>
    String(item.batch_no).toLowerCase().includes(filter.toLowerCase())
  );

  const formatMonthYear = (dateString) => {
    if (!dateString) return "";
    const [year, month] = dateString.split("-");
    return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long" }).format(new Date(year, month - 1));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "numeric", minute: "numeric", hour12: true
    }).format(new Date(dateString));
  };

  /* ── approve ── */
  const approveBatch = (batchData) => {
    const fd = new FormData();
    fd.append("qrId", qrId); fd.append("sku_id", sku.sku_id); fd.append("batchNo", batchData.batch_no);
    fd.append('group_id', sessionStorage.getItem('group_id')); fd.append('name', sessionStorage.getItem('name'));
    fd.append('designation', "User");
    fd.append('uid', sessionStorage.getItem('qrUid')); fd.append('sku_name', sku.brand_name); fd.append('emp_id', sessionStorage.getItem('username'));
    axios.post(baseURL + 'batch/approveBatch', fd, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(() => {
      showToast("Batch approved successfully");
      setShowViewModal(false); fetchDetails(); navigate('/batch-management');
    }).catch(() => showToast("Error approving batch. Please try again.", "error"));
  };

  const updateBatch = (batchData) => { if (updateType === "approval") approveBatch(batchData); };

  /* ── fetch image as data URL ── */
  const fetchImageAsDataURL = async (imageUrl) => {
    const response = await axios.get(imageUrl, { responseType: "arraybuffer" });
    const blob = new Blob([response.data], { type: response.headers["content-type"] });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /* ── delete ── */
  const deleteBatch = (batch) => {
    axios.get(baseURL + 'batch/deleteBatch/' + batch.batch_no + '/' + qrId + '/' + sku.sku_id, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(() => { showToast("Batch deleted successfully"); setisCustomModal(false); fetchDetails(); })
      .catch(() => showToast("Error deleting batch. Please try again.", "error"));
  };

  /* ── reject / remarks ── */
  const reject = (batchData) => {
    const fd = new FormData();
    fd.append("qrId", qrId); fd.append("sku_id", sku.sku_id); fd.append("batchNo", batchData.batch_no);
    fd.append('remarks', (sessionStorage.getItem('name') + " - " + remarks));
    fd.append('group_id', sessionStorage.getItem('group_id')); fd.append('name', sessionStorage.getItem('name'));
    fd.append('designation', "User");
    fd.append('uid', sessionStorage.getItem('qrUid')); fd.append('sku_name', sku.brand_name);
    axios.post(baseURL + 'batch/rejectBatch', fd, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(() => { showToast("Batch rejected"); navigate('/batch-management'); closeModal(); fetchDetails(); })
      .catch(() => showToast("Error rejecting batch.", "error"));
  };

  const submitUpdates = () => {
    if (remarks === "") { setremarksErrors("Remarks cannot be empty."); return; }
    if (updateType === "reject") { reject(selectedBatch); return; }
    const fd = new FormData();
    fd.append('remarks', (sessionStorage.getItem('name') + " - " + remarks));
    fd.append('id', qrId); fd.append('sku_id', sku.sku_id); fd.append("batchNo", batchno);
    fd.append('group_id', sessionStorage.getItem('group_id')); fd.append('name', sessionStorage.getItem('name'));
    fd.append('designation', "User");
    fd.append('uid', sessionStorage.getItem('qrUid')); fd.append('sku_name', sku.brand_name); fd.append('emp_id', sessionStorage.getItem('username'));
    axios.post(baseURL + 'batch/addRemarks_batch', fd, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
    }).then(() => { closeModal(); fetchDetails(); setRemarks(""); });
  };

  const comments = (batchData) => { setComment(batchData.remarks); setShowCommentsModal(true); };
  const update = (batchData) => { sessionStorage.setItem('batchData', JSON.stringify(batchData)); navigate('/update-batch'); };

  /* ── notifications ── */
  const handleNotificationClick = (notification) => {
    const filtered_sku = tableData.filter(item => item.sku_id === notification.payload.sku_id)[0];
    if (!filtered_sku.disabled) {
      sessionStorage.setItem('skuId', JSON.stringify(filtered_sku));
      axios.get(baseURL + "qr/fetchSingleQR/" + filtered_sku.sku_id + "/" + notification.payload.qr_id)
        .then((response) => {
          const d = response.data[0];
          sessionStorage.setItem('qrId', d.qr_id);
          sessionStorage.setItem('qrUid', d.uid);
          sessionStorage.setItem('qrCreatedAt', d.timestamp);
          if (window.location.hash.includes('batch-management')) { fetchDetails(); setShowNotificationModal(false); }
          navigate('/batch-management');
        });
    }
  };

  const changeNotificationStatus = (notification) => {
    axios.get(baseURL + 'notifications/changeNotificationStatus/' + notification.id)
      .then(() => fetchNotification())
      .catch(console.error);
  };

  /* ── pagination ── */
  const totalPages = Math.ceil(filteredTable.length / rowsPerPage);
  const paginatedTable = filteredTable.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handlePageChange = (page) => { if (page > 0 && page <= totalPages) setCurrentPage(page); };

  /* ═══════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════ */
  return (
    <>
      <div className="batchManagement">

        {/* ── Top Bar ── */}
        <div className="dashboard-header">
          <div className="dashboard-actions">
            <button onClick={() => navigate("/dashboard")} className="action-button">
              <FontAwesomeIcon icon={faHome} />
              <span>Home</span>
            </button>
            <button onClick={navigateToDashboard} className="action-button">
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>Back</span>
            </button>
          </div>

          {!isAdmin && (
            <div className="notification-icon">
              {notifications.length > 0 ? (
                <button className="action-button" onClick={() => setShowNotificationModal(true)}>
                  <FontAwesomeIcon icon={faBell} />
                  <span>Notifications</span>
                  <span className="notification-count">{notifications.length}</span>
                </button>
              ) : (
                <button className="action-button" disabled style={{ opacity: .5, cursor: 'default' }}>
                  <FontAwesomeIcon icon={faBell} />
                  <span>Notifications</span>
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Main layout ── */}
        <div className="batch-management-container">

          {/* ── Sidebar ── */}
          <div className="left-section">

            {/* Report */}
           <div className="download-type">
              <button className="download" onClick={() => { generateReport(sku, snappedPdfImage, skuImage, batchTable, qrId) }}>
                <FontAwesomeIcon icon={faPrint} /> View &amp; Print QR Report
              </button>
            </div> 

            {/* QR Info */}
            <div className="info-table">
              <h3>QR Information</h3>
              <table>
                <tbody>
                  <tr><td>QR UID</td><td>{sessionStorage.getItem('qrUid')}</td></tr>
                  <tr><td>Product Name</td><td>{sku.brand_name}</td></tr>
                  <tr><td>GTIN</td><td>{sku.gtin}</td></tr>
                  <tr><td>Generic Name</td><td>{sku.generic_name}</td></tr>
                  <tr><td>Manufacturing Lic. No.</td><td>{sku.manufacturing_license}</td></tr>
                  {isAdmin && (
                    <>
                      <tr><td>Production Unit</td><td>{sku.prod_unit}<br />{sku.address}</td></tr>
                    </>
                  )}
                  <tr><td>List of Excipients</td><td>{sku.excipients}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Right content ── */}
          <div className="right-section">

            {/* Add Batch Form (DE only) */}
            {!isAdmin && (
              <div className="add-batch-form">
                <h3>Add Batch Details</h3>
                <div className="form-inputs">
                  <div>
                    <label>Batch Number</label>
                    <input
                      type="text"
                      id="batchno"
                      className={`form-control ${fieldErrors.batchno ? "is-invalid" : ""}`}
                      value={batchno}
                      onChange={(e) => { setBatchno(e.target.value); setFieldErrors(p => ({ ...p, batchno: '' })); }}
                      placeholder="e.g. B2024-120"
                    />
                    {fieldErrors.batchno && <div className="invalid-feedback d-block">{fieldErrors.batchno}</div>}
                  </div>

                  <div>
                    <label>Manufacturing Date</label>
                    <DatePicker
                      selected={manufactureDate}
                      onChange={(date) => {
                        setManufactureDate(date);
                        setFieldErrors(p => ({ ...p, manufactureDate: '' }));
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      showFourColumnMonthYearPicker
                      placeholderText="Select month / year"
                      className={`custom-yearMonth-input form-control ${fieldErrors.manufactureDate ? "is-invalid" : ""}`}
                      minDate={new Date(2024, 0, 1)}
                      renderCustomHeader={({
                        date,
                        decreaseYear,
                        increaseYear,
                        prevYearButtonDisabled,
                        nextYearButtonDisabled,
                      }) => (
                        <div style={{ color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 10px" }}>
                          <button onClick={decreaseYear} disabled={prevYearButtonDisabled}>{"<"}</button>
                          <span>{date.getFullYear()}</span>
                          <button onClick={increaseYear} disabled={nextYearButtonDisabled}>{">"}</button>
                        </div>
                      )}
                    />
                    {fieldErrors.manufactureDate && <div className="invalid-feedback d-block">{fieldErrors.manufactureDate}</div>}
                  </div>

                  <div>
                    <label>Expiry Date</label>
                    <DatePicker
                      selected={expiryDate}
                      onChange={(date) => {
                        setExpiryDate(date);
                        setFieldErrors(p => ({ ...p, expiryDate: '' }));
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      showFourColumnMonthYearPicker
                      placeholderText="Select month / year"
                      className={`custom-yearMonth-input form-control ${fieldErrors.expiryDate ? "is-invalid" : ""}`}
                      minDate={new Date()}
                      renderCustomHeader={({
                        date,
                        decreaseYear,
                        increaseYear,
                        prevYearButtonDisabled,
                        nextYearButtonDisabled,
                      }) => (
                        <div style={{ color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 10px" }}>
                          <button onClick={decreaseYear} disabled={prevYearButtonDisabled}>{"<"}</button>
                          <span>{date.getFullYear()}</span>
                          <button onClick={increaseYear} disabled={nextYearButtonDisabled}>{">"}</button>
                        </div>
                      )}
                    />
                    {fieldErrors.expiryDate && <div className="invalid-feedback d-block">{fieldErrors.expiryDate}</div>}
                  </div>
                </div>

                <button id="addBatch" onClick={handleAddBatch}>
                  <FontAwesomeIcon icon={faPlus} /> Add Batch
                </button>
              </div>
            )}

            {/* Batch list */}
            <div className="batch-filter-container">
              <h3>
                Batches — {sessionStorage.getItem('qrUid')}
                {sku.brand_name ? ` / ${sku.brand_name}` : ""}
              </h3>

              {/* Filter bar */}
              <div className="filter-table">
                <div className="filter-input">
                  <input
                    type="text"
                    placeholder="Filter by batch number…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                  />
                </div>
              </div>

              {/* Table */}
              <div className="batch-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "14%" }}>Batch No.</th>
                      <th style={{ width: "15%" }}>Mfg. Date</th>
                      <th style={{ width: "15%" }}>Expiry Date</th>
                      <th style={{ width: "14%" }}>Status</th>
                      <th style={{ width: "42%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTable.map((batch, index) => (
                      <tr key={index}>
                        <td className="batch-no-cell">{batch.batch_no}</td>
                        <td>{formatMonthYear(batch.dom)}</td>
                        <td>{formatMonthYear(batch.doe)}</td>
                        <td><StatusPill batch={batch} /></td>
                        <td className="action-buttons">
                          {!isAdmin && (
                            <>
                              {/* PQA approve */}
                              {
                                batch.status === 0 && batch.emp_id !== sessionStorage.getItem('username') && (
                                  <button
                                    className="tab-button approve-btn"
                                    onClick={() => { setShowViewModal(true); setViewModalTitle("Approve"); setSelectedBatch(batch); setUpdateType('approval'); }}
                                  >
                                    <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 11 }} /> Approve
                                  </button>
                                )
                              }
                              {/* Send back */}
                              {
                                batch.status === 0 && batch.emp_id !== sessionStorage.getItem('username') && (
                                  <button
                                    className="tab-button"
                                    onClick={() => { setBatchno(batch.batch_no); openModal(); setModalName("Send Back"); setModalPlaceholder("Enter reason…"); setUpdateType('sendback'); }}
                                  >
                                    <FontAwesomeIcon icon={faUndo} style={{ fontSize: 11 }} /> Send Back
                                  </button>
                                )
                              }
                              {/* Edit (DE) */}
                              {
                                batch.status !== 2 && batch.status !== 1 && batch.emp_id === sessionStorage.getItem('username') && (
                                  <button className="tab-button" onClick={() => update(batch)}>
                                    <FontAwesomeIcon icon={faPencilAlt} style={{ fontSize: 11 }} /> Edit
                                  </button>
                                )
                              }
                              {/* Delete (DE) */}
                              {
                                batch.status !== 2 && batch.status !== 1 && batch.status !== 3 && batch.emp_id === sessionStorage.getItem('username') && (
                                  <button className="tab-button delete-btn" onClick={() => { setSelectedBatch(batch); setisCustomModal(true); }}>
                                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11 }} /> Delete
                                  </button>
                                )
                              }
                            </>
                          )}
                          {/* View remarks */}
                          <button className="tab-button" onClick={() => comments(batch)}>
                            <FontAwesomeIcon icon={faCommentDots} style={{ fontSize: 11 }} /> View Remarks
                          </button>
                          {/* Batch report */}
                          <button className="tab-button" onClick={() => batchReport(batch, skuImage, snappedPdfImage, sku)}>
                            <FontAwesomeIcon icon={faPrint} style={{ fontSize: 11 }} /> View & Print Report
                          </button>
                        </td>
                      </tr>
                    ))}

                    {paginatedTable.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: 'center', padding: '28px 0', color: 'var(--muted)', fontSize: 13 }}>
                          No batches found{filter ? " matching your filter" : ""}.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Pagination */}
                <div className="pagination">
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1}>
                    &larr; Previous
                  </button>
                  <span>Page {currentPage || 0} of {totalPages || 0}</span>
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages}>
                    Next &rarr;
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Remarks / comments view modal ── */}
        {showcommentsModal && (
          <div className="verify-modal">
            <div className="verify-content">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Comment</th>
                    <th style={{ width: 180 }}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {comment.map((comm, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{comm.message}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>{formatDate(comm.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="verify-actions">
                <button className="verify-button" onClick={() => setShowCommentsModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Remarks input modal ── */}
        {isModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>{modalName}</h2>
              <p>Add your note below. It will be visible to relevant team members.</p>
              <textarea
                placeholder={modalPlaceholder}
                value={remarks}
                onChange={(e) => { setRemarks(e.target.value); setremarksErrors(""); }}
              />
              {remarksErrors && <p className="error">{remarksErrors}</p>}
              <div className="modal-buttons">
                <button onClick={submitUpdates}>Submit</button>
                <button onClick={closeModal}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Approve confirm modal ── */}
        {showViewModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>{viewModalTitle} Batch</h2>
              <p>
                Are you sure you want to <strong>{viewModalTitle.toLowerCase()}</strong> batch{" "}
                <code style={{ background: '#F4F8FC', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                  {selectedBatch?.batch_no}
                </code>?
              </p>
              <div className="modal-buttons">
                <button onClick={() => updateBatch(selectedBatch)}>Yes, Confirm</button>
                <button onClick={() => setShowViewModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete confirm modal ── */}
        {isCustomModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <h2>Delete Batch</h2>
              <p>
                This will permanently remove batch{" "}
                <code style={{ background: '#F4F8FC', padding: '2px 6px', borderRadius: 4, fontSize: 12 }}>
                  {selectedBatch?.batch_no}
                </code>. This action cannot be undone.
              </p>
              <div className="modal-buttons">
                <button onClick={() => deleteBatch(selectedBatch)}>Yes, Delete</button>
                <button onClick={() => setisCustomModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Notifications panel ── */}
      {showNotificationModal && (
        <div className="notification-modal">
          <h3>Notifications</h3>
          <div className="notification-content">
            <table>
              <tbody>
                {notifications.map((notification, index) => (
                  <tr key={index} className="notification-item">
                    <td>{notification.type} — {notification.payload.batch_no} for {notification.payload.uid} of {notification.payload.sku_name}</td>
                    <td>
                      {["Batch Added", "Batch Requested for Update", "Batch Updated"].includes(notification.type)
                        ? <button onClick={() => handleNotificationClick(notification)}>View</button>
                        : <button onClick={() => changeNotificationStatus(notification)}>OK</button>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="notification-button" onClick={() => setShowNotificationModal(false)}>Close</button>
        </div>
      )}
    </>
  );
}

export default BatchManagement;